import { Minus, Plus } from 'lucide-react';

export function Stepper({ label, value, min, max, step = 1, suffix = '', onChange }: { label: string; value: number; min: number; max: number; step?: number; suffix?: string; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="font-bold">{label}</span>
      <div className="flex items-center gap-2">
        <button className="btn btn-sm btn-secondary !min-h-10 !px-3" aria-label={`Less ${label}`} disabled={value <= min} onClick={() => onChange(Math.max(min, value - step))}><Minus size={16} /></button>
        <span className="font-display font-black w-16 text-center tabular-nums" aria-live="polite">{value}{suffix}</span>
        <button className="btn btn-sm btn-secondary !min-h-10 !px-3" aria-label={`More ${label}`} disabled={value >= max} onClick={() => onChange(Math.min(max, value + step))}><Plus size={16} /></button>
      </div>
    </div>
  );
}

export function Toggle({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer">
      <span>
        <span className="font-bold block">{label}</span>
        {hint && <span className="text-mute text-sm">{hint}</span>}
      </span>
      <input type="checkbox" role="switch" className="sr-only peer" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span aria-hidden className="relative w-14 h-8 rounded-full bg-ink-3 border border-line peer-checked:bg-[var(--accent)] transition shrink-0 after:absolute after:top-1 after:left-1 after:size-6 after:rounded-full after:bg-paper after:transition peer-checked:after:translate-x-6" />
    </label>
  );
}

export function Segmented<T extends string | number>({ label, value, options, onChange }: { label: string; value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="font-bold">{label}</span>
      <div className="grid gap-1 p-1 rounded-2xl bg-ink" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0,1fr))` }} role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button key={String(o.value)} role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)} className={`rounded-xl py-2 px-2 text-sm font-bold transition ${value === o.value ? 'bg-[var(--accent)] text-ink' : 'text-mute'}`}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
